import {
  createAnalyticsSession,
  type AnalyticsStore,
  type ClickIds,
} from "@repo/analytics";

// Storage keys are private to this web adapter (cookie + sessionStorage).
const VISITOR_ID_COOKIE = "bp_vid";
const CLICK_IDS_STORAGE_KEY = "bp_click_ids";
const VISITOR_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

function readCookie(name: string) {
  if (typeof document === "undefined") {
    return null;
  }

  const prefix = `${name}=`;
  const match = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return match ? decodeURIComponent(match.slice(prefix.length)) : null;
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") {
    return;
  }

  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  const domain =
    typeof window !== "undefined" &&
    window.location.hostname.endsWith("briefpanel.com")
      ? "; Domain=.briefpanel.com"
      : "";

  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${VISITOR_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}${domain}`;
}

function readSessionJson<T>(key: string): T | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeSessionJson(key: string, value: unknown) {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(key, JSON.stringify(value));
}

const webAnalyticsStore: AnalyticsStore = {
  getVisitorId: () => readCookie(VISITOR_ID_COOKIE),
  setVisitorId: (visitorId) => writeCookie(VISITOR_ID_COOKIE, visitorId),
  getClickIds: () => readSessionJson<ClickIds>(CLICK_IDS_STORAGE_KEY),
  setClickIds: (clickIds) => writeSessionJson(CLICK_IDS_STORAGE_KEY, clickIds),
};

export const webAnalyticsSession = createAnalyticsSession(webAnalyticsStore);
